import styled from '@emotion/styled';
import type { ColumnDirection } from '@hotjar/hotsauce';
import { Button, Icon, IconButton, Link, Stack, Table, tokens, Tooltip } from '@hotjar/hotsauce';
import { FEEDBACK } from '@hotjar/webapp/constants/routes';
import { getRoute } from '@hotjar/webapp/utils/routing';
import { DeviceDetails } from '@hotjar/webapp/components/Details/DeviceDetails';
import { BrowserDetails } from '@hotjar/webapp/components/Details/BrowserDetails';
import { OSDetails } from '@hotjar/webapp/components/Details/OSDetails';
import { RecordingLink } from '@hotjar/feedback/components/common/RecordingLink';
import type { FeedbackResponseDto } from '@hotjar/feedback/domain/IncomingFeedback/FeedbackResponseDto';
import { epochDateShort, epochDateTime } from '@hotjar/comments/lib/time';
import { stripDomain } from '@hotjar/webapp/utils/urls';
import { sanitizeUUID } from '@hotjar/webapp/lib/sanitize';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import type { VotingAction } from '@hotjar/feedback/domain/common/Responses/VotingAction';
import { useCurrentOrganization } from '@hotjar/webapp/hooks/useCurrentOrganization';
import { CommentSummary, TextWithLinks } from '@hotjar/feedback';
import type { CountryCode } from '@hotjar/country-lib';
import { getLocalizedCountryNameFromCode } from '@hotjar/country-lib';
import { useCurrentSite } from '@hotjar/site';
import { usePermission } from '@hotjar/webapp/domains/authorization';
import { useNavigation } from '@hotjar/navigation';
import { useTranslation } from '@hotjar/translations';
import type { EmotionStyle } from '../../domain/EmotionStyle';
import { EmotionIcon } from '../../EmotionIcon';
import { Screenshot } from '../Screenshot/Screenshot';
import { ResponseFeedbackTooltip } from '../ResponseFeedbackTooltip';
import { truncateUrl } from '../../utils/truncateUrl';
import { useEmotionLabels } from '../../EmotionIcon/EmotionIcon';

type SortKeys =
  | keyof FeedbackResponseDto
  | 'visitor_uuid'
  | 'content:rating'
  | 'content:message'
  | 'content:email'
  | 'created';

interface SortState {
  key: SortKeys;
  direction: ColumnDirection;
}

export interface ResponsesListProps {
  feedbackId: number;
  responses: FeedbackResponseDto[] | undefined;
  sortString: string;
  onViewResponseClicked: (response: FeedbackResponseDto) => void;
  onDeleteResponseClicked: (respone: FeedbackResponseDto) => void;
  onSortChanged: (sortBy: string, direction: ColumnDirection) => void;
  onFeedbackChanged: (action: VotingAction, responseId: number) => void;
  emotionStyle: EmotionStyle;
  coveragePercentage: number;
}

const FeedbackTable = styled(Table)`
  min-width: max-content;
`;

const Cell = styled(Table.Cell)`
  max-width: 64ch;
`;

const NoWrap = styled.span`
  display: inline-block;
  white-space: nowrap;
`;

const CenteredStack = styled(Stack)`
  align-items: center;
`;

const PageDetails = styled(Link)`
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
  overflow: hidden;
`;

const Flag = styled.div`
  flex-shrink: 0;
`;

const UserIdContainer = styled.span`
  color: ${tokens.color.text_light};
`;

const Blank = styled.span`
  color: ${tokens.color.text_disabled};
  font-style: italic;
`;

const ClickableSpan = styled.span`
  cursor: pointer;
`;

const ClickableDiv = styled.div`
  cursor: pointer;
`;

const StyledEmotionIcon = styled(EmotionIcon)`
  display: flex;
  flex-flow: nowrap;
`;

function UserId({ children }: { children: string | undefined }) {
  const { t } = useTranslation('feedback', { keyPrefix: 'feedback-responses.list.user-id' });
  return (
    <Tooltip text={t('hotjar-user-id')}>
      {children === undefined || children.length === 0 ? (
        <Blank>{t('anonymous')}</Blank>
      ) : (
        <UserIdContainer>{children}</UserIdContainer>
      )}
    </Tooltip>
  );
}

function UserResponse({
  children,
  linkify,
  onClick,
}: {
  children: string | undefined | JSX.Element;
  linkify?: boolean;
  onClick?: () => void;
}) {
  const isBlank =
    typeof children === 'undefined' ||
    (typeof children === 'string' && children.trim().length === 0);

  if (isBlank) return <Blank>Left blank</Blank>;

  const content = linkify ? <TextWithLinks>{children}</TextWithLinks> : children;

  if (onClick) return <ClickableSpan onClick={onClick}>{content}</ClickableSpan>;

  return <>{content}</>;
}

export function ResponsesList({
  feedbackId,
  responses,
  sortString,
  onSortChanged,
  onViewResponseClicked,
  onDeleteResponseClicked,
  onFeedbackChanged,
  emotionStyle,
  coveragePercentage,
}: ResponsesListProps): JSX.Element | null {
  const { t } = useTranslation('feedback', { keyPrefix: 'feedback-responses.list' });
  const { t: commonT } = useTranslation('feedback', { keyPrefix: 'common' });
  const emotionLabels = useEmotionLabels();
  const { data: organization } = useCurrentOrganization();
  const { data: currentSite } = useCurrentSite();
  const sortState: SortState = useMemo(() => {
    const direction = sortString.includes('-') ? 'desc' : 'asc';
    return {
      key: (direction === 'desc' ? sortString.substring(1) : sortString) as SortKeys,
      direction,
    };
  }, [sortString]);

  const SortHeader = ({
    title,
    label,
    sortKey,
    children,
    ...headerProps
  }: {
    title: string;
    label?: string;
    sortKey: string;
    children?: ReactNode | ReactNode[];
  }) => {
    return (
      <Table.HeadCell
        {...headerProps}
        title={title}
        aria-label={label || title}
        isSortable
        sortLabel={t('sort-title-column', { title })}
        sortDirection={sortState.key === sortKey ? sortState.direction : 'none'}
        onSort={(newDirection) => onSortChanged(sortKey, newDirection)}
      >
        {children || title}
      </Table.HeadCell>
    );
  };

  type User = React.ComponentProps<typeof CommentSummary>['users'][number];

  const organizationUsers = useMemo(
    () =>
      organization?.users.reduce<Record<number, User>>((users, user) => {
        return { ...users, [user.id]: user };
      }, {}),
    [organization],
  );

  function createUnknownUser(id: number): User {
    return {
      id,
      name: t('unknown-user'),
      avatar: null,
    };
  }

  const { topBarHeight: topNavHeight } = useNavigation();

  const hasFeedbackResponseReadPermission = usePermission('feedbacks-responses-read');
  const hasFeedbackResponseDeletePermission = usePermission('feedbacks-responses-delete');
  const hasFeedbackResponseVotePermission = usePermission('feedbacks-responses-vote');

  if (currentSite === undefined || responses === undefined || responses.length === 0) {
    return null;
  }

  return (
    <FeedbackTable
      tableName={t('table-name')}
      caption={t('table-caption', { count: responses.length })}
    >
      <Table.Head>
        <Table.HeadRow stickyOffset={topNavHeight}>
          <SortHeader title={t('header.index')} sortKey="index">
            {commonT('number-sign')}
          </SortHeader>
          <SortHeader title={t('header.comments')} sortKey="comment_count">
            {t('header.comments')}
          </SortHeader>
          <SortHeader title={t('header.country')} sortKey="location_id">
            {t('header.country')}
          </SortHeader>
          <SortHeader title={t('header.user')} sortKey="visitor_uuid">
            {t('header.user')}
          </SortHeader>
          <SortHeader title={t('header.page')} sortKey="response_url">
            {t('header.page')}
          </SortHeader>
          <Table.HeadCell>{t('header.screenshot')}</Table.HeadCell>
          <SortHeader title={t('header.rating')} sortKey="question:rating">
            {t('header.rating')}
          </SortHeader>
          <SortHeader title={t('header.feedback')} sortKey="question:message">
            {t('header.feedback')}
          </SortHeader>
          <SortHeader title={t('header.email')} sortKey="question:email">
            {t('header.email')}
          </SortHeader>
          <SortHeader title={t('header.device')} sortKey="device">
            <Tooltip text={t('header.device')}>
              <Icon description={t('header.device')} icon="device_multiple" />
            </Tooltip>
          </SortHeader>
          <SortHeader title={t('header.browser')} sortKey="browser">
            <Tooltip text={t('header.browser')}>
              <Icon description={t('header.browser')} icon="site" />
            </Tooltip>
          </SortHeader>
          <SortHeader title={t('header.operating-system')} sortKey="os">
            <Tooltip text={t('header.operating-system')}>
              <span>{t('header.os')}</span>
            </Tooltip>
          </SortHeader>
          <SortHeader title={t('header.date')} sortKey="created">
            {t('header.date')}
          </SortHeader>
          <Table.HeadCell title={t('header.actions')}>{t('header.actions')}</Table.HeadCell>
        </Table.HeadRow>
      </Table.Head>
      <Table.Body>
        {responses.map((response) => {
          return (
            <Table.Row key={response.id}>
              <Cell>{response.index}</Cell>
              <Cell width="1px">
                {organizationUsers && (
                  <CommentSummary
                    users={response.commented_by.map(
                      (id) => organizationUsers[id] ?? createUnknownUser(id),
                    )}
                    commentCount={response.comment_count}
                    href={getRoute(FEEDBACK.RESPONSE.path, {
                      siteId: currentSite.id,
                      feedbackWidgetId: feedbackId,
                      frid: response.id,
                    })}
                    onClick={() => onViewResponseClicked(response)}
                  />
                )}
              </Cell>
              <Cell>
                <CenteredStack spacing="x1">
                  <Flag className={`flag flag-${response.country_code ?? ''}`} />
                  {response.country_code
                    ? getLocalizedCountryNameFromCode(
                        response.country_code.toUpperCase() as CountryCode,
                      )
                    : t('unknown')}
                </CenteredStack>
              </Cell>
              <Cell>
                <UserId>{sanitizeUUID(response.short_visitor_uuid)}</UserId>
              </Cell>
              <Cell>
                <Tooltip text={response.response_url}>
                  <span>
                    <PageDetails isExternal kind="inline" href={response.response_url}>
                      {truncateUrl(stripDomain(response.response_url))}
                    </PageDetails>
                  </span>
                </Tooltip>
              </Cell>
              <Cell>
                <Screenshot
                  description={t('view-feedback')}
                  thumbnailUrl={response.thumbnail_url}
                  onClick={() => onViewResponseClicked(response)}
                />
              </Cell>
              <Cell>
                <Tooltip text={emotionLabels[response.content.emotion]}>
                  <ClickableDiv onClick={() => onViewResponseClicked(response)}>
                    <StyledEmotionIcon
                      size={emotionStyle === 'star' ? 's' : 'l'}
                      score={response.content.emotion}
                      emotionStyle={emotionStyle}
                    />
                  </ClickableDiv>
                </Tooltip>
              </Cell>
              <Cell>
                <UserResponse linkify onClick={() => onViewResponseClicked(response)}>
                  {response.content.message}
                </UserResponse>
              </Cell>
              <Cell>
                <NoWrap>
                  <UserResponse>{response.content.email}</UserResponse>
                </NoWrap>
              </Cell>
              <Cell>
                <DeviceDetails
                  device={response.device}
                  x={response.window_size[0]}
                  y={response.window_size[1]}
                />
              </Cell>
              <Cell>
                <BrowserDetails browser={response.browser} />
              </Cell>
              <Cell>
                <OSDetails os={response.os} />
              </Cell>
              <Cell>
                <Tooltip text={epochDateTime(response.created_epoch_time)}>
                  <NoWrap>{epochDateShort(response.created_epoch_time)}</NoWrap>
                </Tooltip>
              </Cell>
              <Cell>
                <CenteredStack spacing="x2">
                  {hasFeedbackResponseVotePermission && responses.length > 3 && (
                    <ResponseFeedbackTooltip
                      votingAction={response.voting_action}
                      siteId={currentSite.id}
                      feedbackId={feedbackId}
                      responseId={response.id}
                      onChange={onFeedbackChanged}
                      appendTo={document.body}
                      color={tokens.color.secondary_cta}
                    />
                  )}
                  {hasFeedbackResponseReadPermission && (
                    <Button
                      aria-label={t('view-feedback-id', { id: response.id })}
                      kind="secondary"
                      size="s"
                      onClick={() => onViewResponseClicked(response)}
                    >
                      {t('view-feedback')}
                    </Button>
                  )}

                  <RecordingLink
                    url={response.recording_url}
                    opener="incoming-feedback-response"
                    type="icon"
                    trackTriggerView="feedback.responses"
                    trackUiPosition="incoming_feedback_response_list_view_play_icon"
                    coveragePercentage={coveragePercentage}
                  />
                  {hasFeedbackResponseDeletePermission && (
                    <IconButton
                      size="s"
                      description={t('delete-response-id', { id: response.id })}
                      tooltipText={t('delete-response-id', { id: response.id })}
                      icon="trash"
                      onClick={() => onDeleteResponseClicked(response)}
                    />
                  )}
                </CenteredStack>
              </Cell>
            </Table.Row>
          );
        })}
      </Table.Body>
    </FeedbackTable>
  );
}
